import { useMemo, type FC } from 'react'
import classNames from 'classnames'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../../store/auth/authSlice'
import { type ExternalUserServiceIntegration } from '../../../models/User'
import { useGetUserIntegrationInfoQuery } from '../../../store/user/user'
import { IntegrationServiceId } from '../../../common/integrations'
import { DelegatedEmailIntegrationState } from './DelegatedEmailIntegrationState'
import { Tooltip } from '../../Tooltip/Tooltip'
import styles from './IntegrationState.module.scss'

export type IntegrationStateProps = {
  serviceId: IntegrationServiceId
  currentIntegration?: ExternalUserServiceIntegration
  className?: string
}

const integrationStateOverrides: Partial<Record<IntegrationServiceId, FC<IntegrationStateProps>>> = {
  [IntegrationServiceId.DELEGATED_EMAIL]: DelegatedEmailIntegrationState,
}

const DefaultIntegrationState: FC<IntegrationStateProps> = ({ currentIntegration, className }) => {
  const integrationState: string | undefined = useMemo(() => {
    return currentIntegration?.state
  }, [currentIntegration])
  const integrationType: string | undefined = useMemo(() => {
    return currentIntegration?.type
  }, [currentIntegration])
  const integrationId: string | undefined = useMemo(() => {
    return currentIntegration?.id
  }, [currentIntegration])
  const accountId = useAppSelector(selectAccountId)
  const currentUser = useAppSelector(selectAuthenticatedUser)

  const { data: userIntegrationInfoData, isLoading: userIntegrationInfoIsLoading } = useGetUserIntegrationInfoQuery(
    {
      accountId: accountId,
      userId: currentUser?.id ?? '',
    },
    { skip: !currentUser?.id },
  )
  if ((!integrationState && integrationType !== 'oauth2_app') || !integrationId || !currentUser || !currentIntegration) {
    return null
  }
  if (userIntegrationInfoIsLoading) {
    return null
  }

  if (integrationState === 'live' || integrationType === 'oauth2_app') {
    const connected = (
      <div className={`zocks-text-success ${className}`}>
        <i className='bi bi-check fs-5' />
        <span>Connected</span>
      </div>
    )
    return currentIntegration.email ? (
      <div className='d-flex flex-column'>
        {connected}
        <span className='zocks-text-gray-600 small'>{currentIntegration.email}</span>
      </div>
    ) : (
      connected
    )
  }
  const integrationProblems = userIntegrationInfoData?.data.problems.filter((issue) => issue.integrationId === integrationId) ?? []
  return (
    <div className={classNames(className, 'zocks-text-danger', integrationProblems.length > 0 ? 'overflow-hidden' : null)}>
      <i className='bi bi-exclamation fs-5' />
      {integrationProblems.length > 0 ? (
        <div className='d-flex flex-column gap-1 overflow-hidden'>
          {integrationProblems.map((problem) => (
            <Tooltip
              key={problem.issue}
              placement='bottom'
              tooltipContent={
                <div className='text-start'>
                  <div className='fw-bold'>{problem.issue}</div>
                  {problem.resolution ? <div>{problem.resolution}</div> : null}
                </div>
              }
            >
              <div>
                <div className={classNames('fw-bold', styles.problemTextClamp)}>{problem.issue}</div>
                {problem.resolution ? <div className={classNames('zocks-text-gray-600 small', styles.problemTextClamp)}>{problem.resolution}</div> : null}
              </div>
            </Tooltip>
          ))}
        </div>
      ) : (
        <span>Your integration is invalid. Please reconnect.</span>
      )}
    </div>
  )
}

export const IntegrationState: FC<IntegrationStateProps> = ({ serviceId, currentIntegration, className }) => {
  const Component = integrationStateOverrides[serviceId] ?? DefaultIntegrationState
  return <Component serviceId={serviceId} currentIntegration={currentIntegration} className={className} />
}
